/**
 * Permission keys mirror the backend (`PermissionEntity` / `PermissionVerb` enums): a key is
 * `entity.verb`, `entity.*` grants every verb of an entity and `*` grants everything.
 */
export const PERMISSION_ENTITIES = [
  'organization',
  'workspace',
  'role',
  'user_role',
  'user',
  'merchant',
  'product_import',
  'website',
  'billing',
] as const

export const PERMISSION_VERBS = ['list', 'get', 'create', 'update', 'delete'] as const

export type PermissionEntity = (typeof PERMISSION_ENTITIES)[number]
export type PermissionVerb = (typeof PERMISSION_VERBS)[number]

export type PermissionKey = `${PermissionEntity}.${PermissionVerb}` | `${PermissionEntity}.*` | '*'

export function permissionKey(entity: PermissionEntity, verb: PermissionVerb): PermissionKey {
  return `${entity}.${verb}`
}

/** Permissions the UI checks. Add a key here instead of writing raw strings at call sites. */
export const Permission = {
  OrganizationGet: permissionKey('organization', 'get'),
  OrganizationCreate: permissionKey('organization', 'create'),
  OrganizationUpdate: permissionKey('organization', 'update'),
  WorkspaceCreate: permissionKey('workspace', 'create'),
  WorkspaceUpdate: permissionKey('workspace', 'update'),
  WorkspaceDelete: permissionKey('workspace', 'delete'),
  UserRoleList: permissionKey('user_role', 'list'),
  UserRoleCreate: permissionKey('user_role', 'create'),
  UserRoleDelete: permissionKey('user_role', 'delete'),
  UserList: permissionKey('user', 'list'),
  MerchantList: permissionKey('merchant', 'list'),
  MerchantCreate: permissionKey('merchant', 'create'),
  MerchantUpdate: permissionKey('merchant', 'update'),
  WebsiteList: permissionKey('website', 'list'),
  WebsiteCreate: permissionKey('website', 'create'),
  WebsiteUpdate: permissionKey('website', 'update'),
  BillingGet: permissionKey('billing', 'get'),
  BillingUpdate: permissionKey('billing', 'update'),
} as const satisfies Record<string, PermissionKey>
